import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  accessLocalStorage,
  createIncomingConfigsBatch,
  exportSelectAllEndpoints,
  exportSelectAllQueues,
  getContextsByTenant,
  getIntegrationsByAccountcode
} from '../../../services';
import { getAvailableTrunks } from '../../../services/incomingCallsApi';
import { expandDidRange, DEFAULT_DID_RANGE_MAX } from '../../../services/didRange';
import { scopeByTenant } from '../../../services/tenantScope';

import '../../../libs/bulma.min.css';
import './IncomingCallsForm.css';

import Loading from '../../../components/Loading/LoadingModule';
import TrunkSelector from './components/TrunkSelector';
import AudioSelectorUploads from '../Audio/AudioSelectorUploads';
import MohConfiguration from '../shared/MohConfiguration';
import StasisFallbackInputs from '../shared/StasisFallbackInputs';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faArrowLeft,
  faPhone,
  faInfoCircle,
  faMask,
  faServer,
  faRecordVinyl,
  faVolumeUp,
  faClock,
  faToggleOn,
  faToggleOff,
  faListOl
} from '@fortawesome/free-solid-svg-icons';
import { isDestinationContext } from '../../../services/destinationContexts';
import { integerInputValue, parseIntegerInput } from '../../../services/integerInput';

const RANGE_ERROR_MESSAGES = {
  NOT_NUMERIC: 'DDR inicial e final devem conter apenas dígitos.',
  LENGTH_MISMATCH: 'DDR inicial e final devem ter o mesmo tamanho.',
  ORDER: 'DDR final deve ser maior ou igual ao DDR inicial.',
  LIMIT: `Faixa excede o limite de ${DEFAULT_DID_RANGE_MAX} números por operação.`,
  PREFIX: 'DDR inicial e final devem compartilhar o mesmo prefixo.'
};

function IncomingCallsCreateRange() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [summary, setSummary] = useState(null);
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState('');

  const [values, setValues] = useState({
    tenant: '',
    ddr_start: '',
    ddr_end: '',
    trunk_name: '',
    destination_type_main: 'endpoint',
    destination_main: '',
    stasis_fallback_type: '',
    stasis_fallback_destination: '',
    trunk_name_main: '',
    timeout_main: 60,
    disa_context: '',
    disa_extension: 'menu',
    disa_priority: '1',
    recording_enabled: true,
    recording_format: 'wav',
    protocol_callerid_enabled: false,
    moh_enabled: false,
    moh_class: '',
    audio_enabled: false,
    audio_file: '',
    audio_type: 'ura',
    audio_accountcode: '',
    hangup_after_audio: false,
    description: '',
    active: true
  });

  const [errors, setErrors] = useState({});
  const history = useHistory();

  const destinationTypes = [
    { value: 'endpoint', label: 'Ramal', description: 'Encaminhar para um ramal específico' },
    { value: 'queue', label: 'Fila', description: 'Encaminhar para uma fila de atendimento' },
    { value: 'callcenter-v2', label: 'Callcenter V2', description: 'Fila inteligente via Stasis (CallcenterV2)' },
    { value: 'external', label: 'Externo', description: 'Encaminhar para número externo' },
    { value: 'disa', label: 'DISA', description: 'Acesso direto ao sistema' },
    { value: 'whatsapp', label: 'WhatsApp', description: 'Integração com WhatsApp' },
    { value: 'hangup', label: 'Desligar', description: 'Desligar a chamada' },
    ...(userRole === 'root' ? [
      { value: 'stasis', label: 'Agent IA', description: 'Encaminhar para um agente.' }
    ] : [])
  ];

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 6000);
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const user = await accessLocalStorage.getUserLocalStorage();
        if (!user?.user?.idtenant) {
          history.push('/config/chamadas-entrada');
          return;
        }

        const tenant = user.user.idtenant;
        setUserTenant(tenant);
        setUserRole(user.user.role || '');
        setValues((prev) => ({ ...prev, tenant, audio_accountcode: tenant }));

        const endpointsResult = await exportSelectAllEndpoints();
        if (Array.isArray(endpointsResult)) {
          let fetchedEndpoints = endpointsResult.filter((element) => !isNaN(Number(element.endpoint)));
          fetchedEndpoints = scopeByTenant(fetchedEndpoints, (endpoint) => endpoint.tenant, tenant);
          setAvailableEndpoints(fetchedEndpoints);
        }

        await fetchQueues(tenant);
        await fetchContexts(tenant);
        await fetchTrunks(tenant);
        await checkWhatsAppIntegration(tenant);
      } catch (error) {
        console.error('❌ [INCOMING-CREATE-RANGE] Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [history]);

  useEffect(() => {
    if (values.destination_type_main === 'hangup') {
      setValues((prev) => ({ ...prev, destination_main: '' }));
    } else if (values.destination_type_main === 'whatsapp') {
      setValues((prev) => ({ ...prev, destination_main: 'whatsapp' }));
    } else if (values.destination_type_main === 'stasis') {
      setValues((prev) => ({ ...prev, destination_main: 'stasis' }));
    } else if (values.destination_type_main === 'disa' && availableContexts.length === 1) {
      setValues((prev) => ({ ...prev, disa_context: availableContexts[0].name }));
    }

    if (values.destination_type_main !== 'stasis'
        && (values.stasis_fallback_type || values.stasis_fallback_destination)) {
      setValues((prev) => ({ ...prev, stasis_fallback_type: '', stasis_fallback_destination: '' }));
    }
  }, [values.destination_type_main, availableContexts]);

  const fetchQueues = async (idtenant) => {
    try {
      let queuesList = await exportSelectAllQueues();
      if (queuesList === undefined) return;
      queuesList = scopeByTenant(queuesList, (queue) => queue.accountcodequeue, idtenant);
      queuesList = queuesList ? queuesList.map((s) => ({
        name: s.namequeue,
        displayname: s.descqueue || 'Sem descrição'
      })) : [];
      setAvailableQueues(queuesList);
    } catch (error) {
      console.error('❌ [INCOMING-CREATE-RANGE] Erro ao buscar filas:', error);
      setAvailableQueues([]);
    }
  };

  const fetchContexts = async (tenant) => {
    try {
      const response = await getContextsByTenant(tenant);
      let contexts = [];
      if (Array.isArray(response)) {
        contexts = response;
      } else if (response && response.success && Array.isArray(response.data)) {
        contexts = response.data;
      } else if (response && response.contexts) {
        contexts = response.contexts;
      }
      setAvailableContexts(contexts);
      if (contexts.length === 1) {
        setValues((prev) => ({ ...prev, disa_context: contexts[0].name }));
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE-RANGE] Erro ao buscar contextos:', error);
      setAvailableContexts([]);
    }
  };

  const checkWhatsAppIntegration = async (tenant) => {
    try {
      const response = await getIntegrationsByAccountcode(tenant);
      setHasWhatsAppIntegration(!!(response && response.success && response.data && response.data.length > 0));
    } catch (error) {
      console.error('❌ [INCOMING-CREATE-RANGE] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  const fetchTrunks = async (tenant) => {
    try {
      const response = await getAvailableTrunks(tenant);
      if (response && response.success && Array.isArray(response.data)) {
        const filteredTrunks = response.data.filter((trunk) =>
          trunk.accountcode === tenant || trunk.tenant === tenant);
        setAvailableTrunks(filteredTrunks);
      } else {
        setAvailableTrunks([]);
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE-RANGE] Erro ao buscar troncos:', error);
      setAvailableTrunks([]);
    }
  };

  const handleInputChange = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const handleDdrChange = (field, rawValue) => {
    const digitsOnly = rawValue.replace(/\D/g, '');
    handleInputChange(field, digitsOnly);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!values.tenant?.trim()) newErrors.tenant = 'Tenant é obrigatório';
    if (!values.trunk_name?.trim()) newErrors.trunk_name = 'Tronco é obrigatório para chamadas de entrada';

    const rangeResult = expandDidRange({ start: values.ddr_start, end: values.ddr_end });
    if (!rangeResult.ok) {
      const field = rangeResult.code === 'ORDER' || rangeResult.code === 'LIMIT' ? 'ddr_end' : 'ddr_start';
      newErrors[field] = RANGE_ERROR_MESSAGES[rangeResult.code] || rangeResult.message;
    }

    if (values.destination_type_main === 'endpoint') {
      if (values.destination_main?.trim()) {
        const ramal = values.destination_main.trim();
        if (!/^\d{4}$/.test(ramal)) {
          newErrors.destination_main = 'Ramal deve conter exatamente 4 dígitos numéricos';
        }
      }
    } else if (values.destination_type_main === 'external') {
      if (!values.destination_main?.trim()) newErrors.destination_main = 'Número externo é obrigatório';
      if (!values.trunk_name_main?.trim()) newErrors.trunk_name_main = 'Tronco é obrigatório para chamadas externas';
      if (!values.timeout_main || values.timeout_main < 1 || values.timeout_main > 300) {
        newErrors.timeout_main = 'Timeout deve estar entre 1 e 300 segundos';
      }
    } else if (values.destination_type_main === 'disa') {
      if (!values.disa_context?.trim()) newErrors.disa_context = 'Contexto DISA é obrigatório';
    } else if (values.destination_type_main === 'whatsapp') {
      if (!hasWhatsAppIntegration) newErrors.destination_type_main = 'Nenhuma integração WhatsApp cadastrada para este tenant';
    } else if (values.destination_type_main === 'stasis') {
      if (!values.stasis_fallback_type) {
        newErrors.stasis_fallback_type = 'Tipo de fallback obrigatorio para destino Stasis';
      } else if (values.stasis_fallback_type !== 'hangup' && !values.stasis_fallback_destination?.trim()) {
        newErrors.stasis_fallback_destination = 'Destino do fallback obrigatorio';
      }
    } else if (values.destination_type_main !== 'hangup' && !values.destination_main?.trim()) {
      newErrors.destination_main = 'Destino principal é obrigatório para este tipo de destino';
    }

    if (values.audio_enabled && !values.audio_file?.trim()) {
      newErrors.audio_file = 'Arquivo de áudio é obrigatório quando áudio está habilitado';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSummary(null);

    if (!validateForm()) return;

    try {
      setSaving(true);

      const processedData = { ...values };

      if (values.destination_type_main === 'external' && values.trunk_name_main) {
        processedData.destination_main = `${values.destination_main}@${values.trunk_name_main}`;
      } else if (values.destination_type_main === 'disa' && values.disa_context) {
        processedData.destination_main = `${values.disa_context},${values.disa_extension || 'menu'},${values.disa_priority || '1'}`;
      }

      const result = await createIncomingConfigsBatch(processedData);

      if (result?.success) {
        setSummary(result.data.summary);
        showAlert('success', `Faixa processada: ${result.data.summary.created} criados, ${result.data.summary.existing} já existentes.`);
      } else {
        showAlert('danger', result?.error || 'Erro ao criar faixa de configurações');
      }
    } catch (error) {
      console.error('❌ [INCOMING-CREATE-RANGE] Erro na criação em lote:', error);
      showAlert('danger', 'Erro interno. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const renderDestinationOptions = () => {
    switch (values.destination_type_main) {
      case 'endpoint':
        return availableEndpoints.map((endpoint) => (
          <option key={endpoint.endpoint} value={endpoint.endpoint}>{endpoint.endpoint}</option>
        ));
      case 'queue':
        return availableQueues.map((queue) => (
          <option key={queue.name} value={queue.name}>{queue.name} - {queue.displayname || queue.name}</option>
        ));
      case 'disa':
        return availableContexts.filter((context) => {
          const contextName = (context.name || '').toLowerCase();
          return isDestinationContext(contextName);
        }).map((context) => (
          <option key={context.name} value={context.name}>{context.label}</option>
        ));
      default:
        return null;
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="message-list-container">
      {alert.show && (
        <div className={`notification is-${alert.type}`}>
          <button className="delete" onClick={() => setAlert({ ...alert, show: false })}></button>
          <div style={{ whiteSpace: 'pre-line' }}>{alert.message}</div>
        </div>
      )}

      <div className="message-list-header">
        <div className="message-list-title-wrapper">
          <div className="message-list-title-left">
            <FontAwesomeIcon icon={faListOl} className="message-list-icon" />
            <div>
              <h1 className="message-list-title">Cadastrar Faixa de DDRs</h1>
              <p className="message-list-subtitle">Crie vários DDRs de uma vez com um destino padrão comum</p>
            </div>
          </div>
          <div className="message-list-actions">
            <button className="btn-new-message" style={{ background: '#e5e7eb', color: '#2c3e50' }} onClick={() => history.push('/config/chamadas-entrada')}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Voltar
            </button>
          </div>
        </div>
      </div>

      <div className="columns is-centered">
        <div className="column is-8">
          <div className="box">

            {summary && (
              <div className="notification is-info is-light">
                <p><strong>Resumo da última operação</strong></p>
                <p>Solicitados: {summary.requested} · Criados: {summary.created} · Já existentes: {summary.existing} · Erros: {summary.errors}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="field">
                <label className="label">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                  Cliente <span className="has-text-danger">*</span>
                </label>
                <div className="control">
                  <input className="input" type="text" value={values.tenant} disabled />
                </div>
                <p className="help">Feixe do usuário logado (não editável)</p>
              </div>

              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faMask} className="mr-2" />
                      DDR Inicial *
                    </label>
                    <div className="control">
                      <input
                        className={`input ${errors.ddr_start ? 'is-danger' : ''}`}
                        type="text"
                        placeholder="Ex: 1433334000"
                        value={values.ddr_start}
                        onChange={(e) => handleDdrChange('ddr_start', e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    {errors.ddr_start && <p className="help is-danger">{errors.ddr_start}</p>}
                  </div>
                </div>
                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faMask} className="mr-2" />
                      DDR Final *
                    </label>
                    <div className="control">
                      <input
                        className={`input ${errors.ddr_end ? 'is-danger' : ''}`}
                        type="text"
                        placeholder="Ex: 1433334099"
                        value={values.ddr_end}
                        onChange={(e) => handleDdrChange('ddr_end', e.target.value)}
                        maxLength={50}
                      />
                    </div>
                    {errors.ddr_end && <p className="help is-danger">{errors.ddr_end}</p>}
                  </div>
                </div>
              </div>
              <p className="help">
                Ambos devem ter o mesmo tamanho e prefixo, e o final deve ser maior ou igual ao inicial.
                Limite de {DEFAULT_DID_RANGE_MAX} números por operação. Números já cadastrados para este tenant
                são ignorados e reportados no resumo, não impedem a criação dos demais.
              </p>

              <TrunkSelector
                selectedTrunk={values.trunk_name}
                onTrunkChange={(value) => handleInputChange('trunk_name', value)}
                tenant={userTenant}
                error={errors.trunk_name}
                required={true}
              />

              <div className="field">
                <label className="label">Tipo de Destino Principal *</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select
                      value={values.destination_type_main}
                      onChange={(e) => handleInputChange('destination_type_main', e.target.value)}
                    >
                      {destinationTypes.map((type) => (
                        <option
                          key={type.value}
                          value={type.value}
                          disabled={type.value === 'whatsapp' && !hasWhatsAppIntegration}
                        >
                          {type.value === 'whatsapp' && !hasWhatsAppIntegration
                            ? `${type.label} (sem integração)`
                            : `${type.label} - ${type.description}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="label">
                  <FontAwesomeIcon icon={faPhone} className="mr-2" />
                  Destino Principal
                  {values.destination_type_main !== 'endpoint' &&
                   values.destination_type_main !== 'hangup' &&
                   values.destination_type_main !== 'whatsapp' &&
                   values.destination_type_main !== 'stasis' &&
                   <span className="has-text-danger">*</span>}
                </label>
                <div className="control">
                  {values.destination_type_main === 'hangup' ? (
                    <input className="input" type="text" value="" placeholder="Chamada será desligada" disabled />
                  ) : values.destination_type_main === 'whatsapp' ? (
                    <input className="input" type="text" value="WhatsApp AGI" disabled />
                  ) : values.destination_type_main === 'stasis' ? (
                    <input className="input" type="text" value="App Stasis (voice-gateway-ai)" disabled />
                  ) : values.destination_type_main === 'endpoint' ? (
                    <input
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text"
                      placeholder="Ex: 1000, 1001, 1234"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      maxLength={4}
                    />
                  ) : values.destination_type_main === 'queue' ? (
                    <div className="select is-fullwidth">
                      <select
                        className={errors.destination_main ? 'is-danger' : ''}
                        value={values.destination_main}
                        onChange={(e) => handleInputChange('destination_main', e.target.value)}
                      >
                        <option value="">Selecione uma fila</option>
                        {renderDestinationOptions()}
                      </select>
                    </div>
                  ) : values.destination_type_main === 'disa' ? (
                    <div className="select is-fullwidth">
                      <select
                        className={errors.disa_context ? 'is-danger' : ''}
                        value={values.disa_context}
                        onChange={(e) => handleInputChange('disa_context', e.target.value)}
                      >
                        <option value="">Selecione um contexto</option>
                        {renderDestinationOptions()}
                      </select>
                    </div>
                  ) : values.destination_type_main === 'callcenter-v2' ? (
                    <input
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text"
                      placeholder="Ex: suporte-mlz, vendas-mlz"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value)}
                    />
                  ) : (
                    <input
                      className={`input ${errors.destination_main ? 'is-danger' : ''}`}
                      type="text"
                      placeholder="Ex: 1134567890"
                      value={values.destination_main}
                      onChange={(e) => handleInputChange('destination_main', e.target.value)}
                    />
                  )}
                </div>
                {errors.destination_main && <p className="help is-danger">{errors.destination_main}</p>}
                {errors.disa_context && <p className="help is-danger">{errors.disa_context}</p>}
              </div>

              {values.destination_type_main === 'stasis' && (
                <StasisFallbackInputs
                  fallbackType={values.stasis_fallback_type}
                  fallbackDestination={values.stasis_fallback_destination}
                  onChange={handleInputChange}
                  availableEndpoints={availableEndpoints}
                  availableQueues={availableQueues}
                  availableContexts={availableContexts}
                  errorType={errors.stasis_fallback_type}
                  errorDestination={errors.stasis_fallback_destination}
                />
              )}

              {(values.destination_type_main === 'endpoint' ||
                values.destination_type_main === 'queue' ||
                values.destination_type_main === 'external') && (
                <div className="field">
                  <label className="label">
                    <FontAwesomeIcon icon={faClock} className="mr-2" />
                    Timeout (segundos)
                  </label>
                  <div className="control">
                    <input
                      className={`input ${errors.timeout_main ? 'is-danger' : ''}`}
                      type="number"
                      min="1"
                      max="300"
                      value={integerInputValue(values.timeout_main)}
                      onChange={(e) => handleInputChange('timeout_main', parseIntegerInput(e.target.value))}
                    />
                  </div>
                  {errors.timeout_main && <p className="help is-danger">{errors.timeout_main}</p>}
                </div>
              )}

              {values.destination_type_main === 'external' && (
                <div className="field">
                  <label className="label">
                    <FontAwesomeIcon icon={faServer} className="mr-2" />
                    Tronco de Saída *
                  </label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={values.trunk_name_main}
                        onChange={(e) => handleInputChange('trunk_name_main', e.target.value)}
                        className={errors.trunk_name_main ? 'is-danger' : ''}
                      >
                        <option value="">Selecione o tronco</option>
                        {availableTrunks.map((trunk) => (
                          <option key={trunk.name} value={trunk.name}>{trunk.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {errors.trunk_name_main && <p className="help is-danger">{errors.trunk_name_main}</p>}
                </div>
              )}

              <div className="columns">
                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faRecordVinyl} className="mr-2" />
                      Gravação de Chamadas
                    </label>
                    <div className="control">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={values.recording_enabled}
                          onChange={(e) => handleInputChange('recording_enabled', e.target.checked)}
                        />
                        <span className="ml-2">Habilitar gravação</span>
                      </label>
                    </div>
                    <div className="control mt-2">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={values.protocol_callerid_enabled}
                          onChange={(e) => handleInputChange('protocol_callerid_enabled', e.target.checked)}
                          disabled={!values.recording_enabled}
                        />
                        <span className="ml-2">Enviar protocolo na chamada</span>
                      </label>
                      <p className="help">
                        {values.recording_enabled
                          ? 'O protocolo substitui o nome do chamador no identificador do atendente, inclusive no Callcenter V2 e nas integrações.'
                          : 'Requer gravação habilitada'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="column is-6">
                  <div className="field">
                    <label className="label">
                      <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
                      Reprodução de Áudio
                    </label>
                    <div className="control">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={values.audio_enabled}
                          onChange={(e) => handleInputChange('audio_enabled', e.target.checked)}
                        />
                        <span className="ml-2">Reproduzir áudio antes do encaminhamento</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="columns mt-4">
                <div className="column">
                  <MohConfiguration
                    config={{ moh_enabled: values.moh_enabled || false, moh_class: values.moh_class || '' }}
                    tenant={userTenant}
                    onChange={(mohConfig) => {
                      handleInputChange('moh_enabled', mohConfig.moh_enabled);
                      handleInputChange('moh_class', mohConfig.moh_class);
                    }}
                    disabled={saving}
                  />
                </div>
              </div>

              {values.audio_enabled && (
                <div className="field">
                  <label className="label">Arquivo de Áudio *</label>
                  <AudioSelectorUploads
                    tenant={userTenant}
                    selectedFile={values.audio_file}
                    selectedType={values.audio_type}
                    onAudioChange={(audioData) => {
                      handleInputChange('audio_file', audioData.file);
                      handleInputChange('audio_type', audioData.type);
                      handleInputChange('audio_accountcode', audioData.accountcode);
                    }}
                    error={errors.audio_file}
                  />
                  {errors.audio_file && <p className="help is-danger">{errors.audio_file}</p>}

                  <div className="field mt-4">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={values.hangup_after_audio}
                        onChange={(e) => handleInputChange('hangup_after_audio', e.target.checked)}
                      />
                      <span className="ml-2">Desligar após reproduzir áudio</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="field">
                <label className="label">Descrição</label>
                <div className="control">
                  <textarea
                    className="textarea"
                    placeholder="Descrição opcional aplicada a todos os DDRs da faixa"
                    value={values.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows="3"
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Status</label>
                <div className="control">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={values.active}
                      onChange={(e) => handleInputChange('active', e.target.checked)}
                    />
                    <span className="ml-2">
                      <FontAwesomeIcon
                        icon={values.active ? faToggleOn : faToggleOff}
                        className={`mr-1 ${values.active ? 'has-text-success' : 'has-text-grey'}`}
                      />
                      Configuração ativa
                    </span>
                  </label>
                </div>
              </div>

              <div className="field is-grouped is-grouped-right">
                <div className="control">
                  <button type="button" className="button is-light" onClick={() => history.push('/config/chamadas-entrada')}>
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Cancelar
                  </button>
                </div>
                <div className="control">
                  <button type="submit" className={`button is-primary ${saving ? 'is-loading' : ''}`} disabled={saving}>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Criar Faixa
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncomingCallsCreateRange;
